import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import PublicLayout from "@/pages/public/PublicLayout";
import Home from "@/pages/public/Home";
import ContentDetail from "@/pages/public/ContentDetail";
import Category from "@/pages/public/Category";
import Section from "@/pages/public/Section";
import CrimeWatch from "@/pages/public/CrimeWatch";
import CrimeDetail from "@/pages/public/CrimeDetail";
import Search from "@/pages/public/Search";
import NewsletterManage from "@/pages/public/NewsletterManage";
import About from "@/pages/public/About";
import NotFound from "@/pages/public/NotFound";

import Login from "@/pages/command/Login";
import CommandLayout from "@/pages/command/CommandLayout";
import Dashboard from "@/pages/command/Dashboard";
import Projects from "@/pages/command/Projects";
import ProjectDetail from "@/pages/command/ProjectDetail";
import Sources from "@/pages/command/Sources";
import Claims from "@/pages/command/Claims";
import ClaimDetail from "@/pages/command/ClaimDetail";
import Studio from "@/pages/command/Studio";
import ContentEditor from "@/pages/command/ContentEditor";
import Editorial from "@/pages/command/Editorial";
import Audit from "@/pages/command/Audit";
import CrimeAdmin from "@/pages/command/CrimeAdmin";
import { Scripts, Quotes, Subscribers } from "@/pages/command/Studios";
import EmailStudio from "@/pages/command/EmailStudio";
import MediaLibrary from "@/pages/command/MediaLibrary";
import GhanaIntel from "@/pages/command/ghana/GhanaIntel";
import GhanaIntelDetail from "@/pages/command/ghana/GhanaIntelDetail";
import GhanaBriefs from "@/pages/command/ghana/GhanaBriefs";
import ClaimTracker from "@/pages/command/tracker/ClaimTracker";
import ClaimTrackerDetail from "@/pages/command/tracker/ClaimTrackerDetail";
import PromiseTracker from "@/pages/command/promise/PromiseTracker";
import PromiseTrackerDetail from "@/pages/command/promise/PromiseTrackerDetail";
import TimelineBuilder from "@/pages/command/timeline/TimelineBuilder";
import TimelineDetail from "@/pages/command/timeline/TimelineDetail";
import EntityProfiles from "@/pages/command/entity/EntityProfiles";
import EntityDetail from "@/pages/command/entity/EntityDetail";
import BusinessIntel from "@/pages/command/business/BusinessIntel";
import BusinessIntelDetail from "@/pages/command/business/BusinessIntelDetail";
import SportsIntel from "@/pages/command/sports/SportsIntel";
import LiveData from "@/pages/command/livedata/LiveData";
import EntertainmentIntel from "@/pages/command/entertainment/EntertainmentIntel";
import EntRecordDetail from "@/pages/command/entertainment/EntRecordDetail";
import CultureIntel from "@/pages/command/culture/CultureIntel";
import CultRecordDetail from "@/pages/command/culture/CultRecordDetail";
import CultureGraph from "@/pages/command/culture/CultureGraph";
import CultRelationships from "@/pages/command/culture/CultRelationships";
import CultReconciliation from "@/pages/command/culture/CultReconciliation";
import SportsIntelDetail from "@/pages/command/sports/SportsIntelDetail";
import ResetPassword from "@/pages/command/ResetPassword";
import ConfirmSubscription from "@/pages/public/ConfirmSubscription";
import Unsubscribe from "@/pages/public/Unsubscribe";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null)
    return (
      <div className="flex h-screen items-center justify-center bg-[#0A0A0A] text-zinc-400 font-mono-data text-sm">
        Authenticating…
      </div>
    );
  if (!user) return <Navigate to="/command-center/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public site */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/category/:slug" element={<Category />} />
              <Route path="/business" element={<Section category="Business" description="Economy, markets, companies and money stories — verified and in context." />} />
              <Route path="/sports" element={<Section category="Sports" description="Football, basketball and beyond — reporting, analysis and fact-checks." />} />
              <Route path="/entertainment" element={<Section category="Entertainment" description="Music, film, celebrity and the creative industries." />} />
              <Route path="/culture" element={<Section category="Culture" description="Heritage, arts, identity and the ideas shaping society." />} />
              <Route path="/article/:slug" element={<ContentDetail />} />
              <Route path="/analysis/:slug" element={<ContentDetail />} />
              <Route path="/fact-check/:slug" element={<ContentDetail />} />
              <Route path="/global-crime-watch" element={<CrimeWatch />} />
              <Route path="/global-crime-watch/:slug" element={<CrimeDetail />} />
              <Route path="/search" element={<Search />} />
              <Route path="/newsletter/manage" element={<NewsletterManage />} />
              <Route path="/newsletter/confirm" element={<ConfirmSubscription />} />
              <Route path="/newsletter/unsubscribe" element={<Unsubscribe />} />
            </Route>

            {/* Command center */}
            <Route path="/command-center/login" element={<Login />} />
            <Route path="/command-center/reset-password" element={<ResetPassword />} />
            <Route
              path="/command-center"
              element={
                <Protected>
                  <CommandLayout />
                </Protected>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
              <Route path="sources" element={<Sources />} />
              <Route path="claims" element={<Claims />} />
              <Route path="claims/:id" element={<ClaimDetail />} />
              <Route path="ghana-intel" element={<GhanaIntel />} />
              <Route path="ghana-intel/briefs" element={<GhanaBriefs />} />
              <Route path="ghana-intel/:id" element={<GhanaIntelDetail />} />
              <Route path="claim-tracker" element={<ClaimTracker />} />
              <Route path="claim-tracker/:id" element={<ClaimTrackerDetail />} />
              <Route path="promise-tracker" element={<PromiseTracker />} />
              <Route path="promise-tracker/:id" element={<PromiseTrackerDetail />} />
              <Route path="timelines" element={<TimelineBuilder />} />
              <Route path="timelines/:id" element={<TimelineDetail />} />
              <Route path="entities" element={<EntityProfiles />} />
              <Route path="entities/:id" element={<EntityDetail />} />
              <Route path="business-intelligence" element={<BusinessIntel />} />
              <Route path="business-intelligence/:id" element={<BusinessIntelDetail />} />
              <Route path="sports-intelligence" element={<SportsIntel />} />
              <Route path="sports-intelligence/:id" element={<SportsIntelDetail />} />
              <Route path="live-data" element={<LiveData />} />
              <Route path="entertainment" element={<EntertainmentIntel />} />
              <Route path="entertainment/:id" element={<EntRecordDetail />} />
              <Route path="culture" element={<CultureIntel />} />
              <Route path="culture/graph" element={<CultureGraph />} />
              <Route path="culture/relationships" element={<CultRelationships />} />
              <Route path="culture/reconciliation" element={<CultReconciliation />} />
              <Route path="culture/:id" element={<CultRecordDetail />} />
              <Route path="studio" element={<Studio />} />
              <Route path="studio/:id" element={<ContentEditor />} />
              <Route path="editorial" element={<Editorial />} />
              <Route path="crime" element={<CrimeAdmin />} />
              <Route path="scripts" element={<Scripts />} />
              <Route path="quotes" element={<Quotes />} />
              <Route path="subscribers" element={<Subscribers />} />
              <Route path="email" element={<EmailStudio />} />
              <Route path="media" element={<MediaLibrary />} />
              <Route path="audit" element={<Audit />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
